import React, { memo, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import cn from 'classnames';

import { ButtonV3, DropdownV3, InputV2 } from 'components/ComponentV2';
import Icon from 'components/Icon';
import Tooltip from 'components/Tooltip';
import { Col, Row } from 'components/layout';
import CancelButton from 'components/CancelButton';
import { Option } from 'lib/models/option';

import Gamification, { GamificationProps } from '../Gamification';
import SectionWrapper from '../SectionWrapper';

import styles from './CompanyDetails.module.scss';

interface CompanyDetailsProps extends GamificationProps {
  employeesCountRange?: Option[];
  industryOptions?: Option[];
  showProgressBanner: boolean;
  sectionClass?: string;
}

export const CompanyDetails: React.FC<CompanyDetailsProps> = memo(
  ({
    employeesCountRange,
    industryOptions,
    showProgressBanner,
    progressDetails,
    profileCompletePercent,
    nonCompletedSection,
    handleProfileCompletionButton,
    handleNextButton,
    showNextButton,
    sectionClass,
  }) => {
    const {
      register,
      watch,
      setValue,
      formState: { errors },
    } = useFormContext(); // retrieve all hook methods

    const [showProgressBannerState, setShowProgressBannerState] =
      React.useState<boolean>(showProgressBanner);

    useEffect(() => {
      if (showProgressBannerState === false)
        setShowProgressBannerState(showProgressBanner);
    }, [showProgressBanner]);

    const subIndustry: string[] = watch('sub_industry_id');
    const industryName: string = watch('custom_industry_name');

    const [showSubIndustry, setShowSubIndustry] = useState<boolean>(
      !!subIndustry
    );

    const [showIndustryNameInput, setShowIndustryNameInput] = useState(
      !!industryName
    );

    const currentIndustryId = watch('industry_id');
    const currentSubIndustryId = watch('sub_industry_id');
    const currentEmployeesCount = watch('employees_count');
    const currentFoundedYear = watch('founded_year');

    const currentYear = new Date().getFullYear();
    const foundedYearOption = useMemo(() => {
      const options = [];
      for (let year = 1900; year <= currentYear; year++) {
        const yearStr = year.toString();
        options.push({
          id: yearStr,
          label: yearStr,
          value: yearStr,
        });
      }
      return options;
    }, []);

    const selectedEmployeesCount =
      employeesCountRange?.find((count) => count.id === currentEmployeesCount)
        ?.label || '';

    const selectedFoundedYear =
      foundedYearOption.find((year) => year?.id === currentFoundedYear)
        ?.label || '';

    const selectedIndustry =
      industryOptions?.find((industry) => industry?.id === currentIndustryId)
        ?.label || '';

    const selectedSubIndustry =
      industryOptions?.find((industry) => industry?.id === currentSubIndustryId)
        ?.label || '';

    const handleSubIndustryDelete = () => {
      setValue('sub_industry_id', '');
      setShowSubIndustry(false);
    };

    return (
      <div id={`section_${progressDetails?.id}`} className={sectionClass}>
        {showProgressBannerState && (
          <Gamification
            profileCompletePercent={profileCompletePercent}
            nonCompletedSection={nonCompletedSection}
            progressDetails={progressDetails}
            handleProfileCompletionButton={() => {
              setShowProgressBannerState(false);
              handleProfileCompletionButton();
            }}
            handleNextButton={() => {
              setShowProgressBannerState(false);
              handleNextButton && handleNextButton();
            }}
            showNextButton={showNextButton}
          />
        )}
        <SectionWrapper
          title="Company details"
          description="Add your company details to give candidates a core understanding of your organisation."
        >
          <Row>
            <Col span={24} className={styles.CompanyDetailsFormColumn}>
              <InputV2
                label="HQ location"
                {...register('address', {
                  required: 'HQ location is required',
                })}
                isInvalid={!!errors.address}
                helperText={errors.address?.message}
                labelClassName={styles.labelField}
                inputClassName={styles.inputField}
                placeholder="Marylebone, London, UK"
              />
            </Col>
            {employeesCountRange && (
              <Col span={24} className={styles.CompanyDetailsFormColumn}>
                <DropdownV3
                  title="Company size"
                  label="Company size"
                  value={
                    employeesCountRange
                      .find((option) => option.id === currentEmployeesCount)
                      ?.value.toString() || ''
                  }
                  onChange={(value: string) => {
                    setValue(
                      'employees_count',
                      employeesCountRange.find(
                        (option) => option.value === value
                      )?.id
                    );
                  }}
                  options={employeesCountRange}
                  isSearchable={true}
                  searchPlaceholder="Search"
                  selectedValueLabel={selectedEmployeesCount}
                  isValid={!errors.employees_count}
                  helperText="Company size is required"
                  labelClassName={styles.labelField}
                  className={styles.CompanyDropDown}
                  triggerButtonClassName={styles.triggerButton}
                />
              </Col>
            )}
            <Col span={24} className={styles.CompanyDetailsFormColumn}>
              <DropdownV3
                title="Founded year"
                label="Founded year"
                value={currentFoundedYear}
                onChange={(value: string) => setValue('founded_year', value)}
                options={foundedYearOption}
                isSearchable={true}
                searchPlaceholder="Search"
                selectedValueLabel={selectedFoundedYear}
                isValid={!errors.founded_year}
                helperText="Founded year is required"
                labelClassName={styles.labelField}
                className={styles.CompanyDropDown}
                triggerButtonClassName={styles.triggerButton}
              />
            </Col>
            {industryOptions && (
              <>
                <Col
                  span={24}
                  className={cn(
                    {
                      [styles.CompanyDetailsFormColumn]: showIndustryNameInput,
                    },
                    { [styles.hideMargin]: !showIndustryNameInput }
                  )}
                >
                  <DropdownV3
                    title="Industry"
                    label="Industry"
                    value={
                      industryOptions
                        .find((option) => option.id === currentIndustryId)
                        ?.value.toString() || ''
                    }
                    onChange={(value: string) => {
                      setValue(
                        'industry_id',
                        industryOptions.find((option) => option.value === value)
                          ?.id
                      );
                    }}
                    options={industryOptions}
                    isSearchable={true}
                    searchPlaceholder="Search"
                    selectedValueLabel={selectedIndustry}
                    isValid={!errors.industry_id}
                    helperText="Industry is required"
                    labelClassName={styles.labelField}
                    className={styles.CompanyDropDown}
                    triggerButtonClassName={styles.triggerButton}
                  />
                </Col>
                {showIndustryNameInput ? (
                  <Col span={24} className={styles.CompanyDetailsFormColumn}>
                    <div className={styles.wrapper}>
                      <InputV2
                        label="Industry name"
                        {...register('custom_industry_name', {
                          maxLength: 36,
                        })}
                        isInvalid={!!errors.custom_industry_name}
                        helperText={errors.custom_industry_name?.message}
                        labelClassName={styles.labelField}
                        inputClassName={styles.inputField}
                        placeholder="Add specific industry name"
                        showOptionalLabel={true}
                        infoToolTip={
                          <Tooltip
                            content="Add a more specific industry name to show on your profile. Candidates still get matched to the pre-defined industries, so choose them carefully."
                            position="top"
                            xAlign="center"
                            color="dark"
                            className={cn(styles.tooltipContainer)}
                            hasWhitespaceNoWrap={false}
                          >
                            <Icon iconName="icon_info" size="xsmall" />
                          </Tooltip>
                        }
                      />
                      <CancelButton
                        onRemove={() => {
                          setValue('custom_industry_name', '');
                          setShowIndustryNameInput(false);
                        }}
                        isShow={true}
                        placeholderClassName={styles.closeIcon}
                        className={styles.closeButton}
                      />
                    </div>
                  </Col>
                ) : (
                  <ButtonV3
                    variant="text"
                    color="blue"
                    className={styles.addIndustryButton}
                    onClick={() => setShowIndustryNameInput(true)}
                  >
                    Add specific industry name
                  </ButtonV3>
                )}
                <Col span={24} className={styles.CompanyDetailsFormColumn}>
                  {showSubIndustry ? (
                    <div className={styles.subIndustryDropdown}>
                      <DropdownV3
                        title="Sub-Industry"
                        label="Sub-Industry"
                        value={
                          industryOptions
                            .find(
                              (option) => option.id === currentSubIndustryId
                            )
                            ?.value.toString() || ''
                        }
                        onChange={(value: string) =>
                          setValue(
                            'sub_industry_id',
                            industryOptions.find(
                              (option) => option.value === value
                            )?.id
                          )
                        }
                        options={industryOptions.filter(
                          (industry) => industry.id !== currentIndustryId
                        )}
                        isSearchable={true}
                        searchPlaceholder="Search"
                        selectedValueLabel={selectedSubIndustry}
                        isValid={!errors.sub_industry_id}
                        helperText="SubIndustry is required"
                        labelClassName={styles.labelField}
                        className={styles.CompanyDropDown}
                        triggerButtonClassName={styles.triggerButton}
                        showOptionalLabel={true}
                        infoToolTip={
                          <Tooltip
                            content="This helps us match your opportunities to students who are interested in other focus areas of your company."
                            position="top"
                            xAlign="center"
                            color="dark"
                            className={styles.tooltipContainer}
                            hasWhitespaceNoWrap={false}
                          >
                            <Icon iconName="icon_info" size="xsmall" />
                          </Tooltip>
                        }
                      />
                      <CancelButton
                        onRemove={handleSubIndustryDelete}
                        isShow={true}
                        placeholderClassName={styles.closeIcon}
                        className={styles.closeButton}
                      />
                    </div>
                  ) : (
                    <div className={styles.addSubIndustry}>
                      <ButtonV3
                        onClick={() => {
                          setShowSubIndustry(true);
                        }}
                        className={styles.addNewButton}
                        startIcon="plus"
                        variant="contained"
                        color="secondary"
                        type="button"
                      >
                        Add sub-industry
                      </ButtonV3>
                      <Tooltip
                        content="This helps us match your opportunities to students who are interested in other focus areas of your company."
                        position="top"
                        xAlign="center"
                        color="dark"
                        className={styles.tooltipContainer}
                      >
                        <Icon
                          iconName="info"
                          className={styles.tooltipContainerIcon}
                          size="large"
                        />
                      </Tooltip>
                    </div>
                  )}
                </Col>
              </>
            )}
          </Row>
        </SectionWrapper>
      </div>
    );
  }
);

CompanyDetails.displayName = 'CompanyDetails';
